import { drawExtensionIcon } from '@/extensions/pixelIcons';
import { z } from 'zod';
import { configureTile } from '../configuration';
import type { DigitilesExtension } from '../catalog';
import { InputType, type TileDefinition } from '@/models/tiles/TileDefinition';
import { CircuitType, Side } from '@/schemas/common';
import { TileSideIOType } from '@/schemas/tiles';
import { standardBuildAndConnect } from '@/models/logic/utils';
import {
  defaultTileGraphics,
  connectionGraphics,
} from '@/models/tiles/DefaultTileGraphics';
import { colors } from '@/styles';
export const PORTAL_SEND = 'extension:digitiles.portal/send' as const;
export const PORTAL_RECEIVE = 'extension:digitiles.portal/receive' as const;
const channelSchema = z.string().trim().min(1).max(64);
const stateSchema = z.object({ channel: channelSchema });
type Config = z.infer<typeof stateSchema>;
function portal(direction: 'send' | 'receive'): TileDefinition<Config> {
  const type = direction === 'send' ? PORTAL_SEND : PORTAL_RECEIVE;
  const ioSides = {
    [Side.TOP]: { type: TileSideIOType.NONE, forceConnection: false },
    [Side.RIGHT]: {
      type: direction === 'receive' ? TileSideIOType.OUTPUT : TileSideIOType.NONE,
      forceConnection: direction === 'receive',
    },
    [Side.BOTTOM]: { type: TileSideIOType.NONE, forceConnection: false },
    [Side.LEFT]: {
      type: direction === 'send' ? TileSideIOType.INPUT : TileSideIOType.NONE,
      forceConnection: direction === 'send',
    },
  };
  const tile: TileDefinition<Config> = {
    type,
    name: direction === 'send' ? 'Portal sender' : 'Portal receiver',
    canRotate: true,
    stateSchema,
    createDefaultState: () => ({ channel: 'A' }),
    graphConfiguration: (state) => ({ channel: state.channel }),
    wirelessChannel: (state) => ({ channel: state.channel, direction }),
    ioSides,
    buildAndConnectNodes: (data) => standardBuildAndConnect(type, data, tile),
    createContextMenuItems: ({ instance, chipEditorModel }) => [
      {
        label: `Channel (${instance.state.channel})`,
        action: async (takeInput) => {
          try {
            const channel = await takeInput(
              InputType.STRING,
              'Portal channel',
              instance.state.channel,
              true,
              (value) => ({
                isValid: channelSchema.safeParse(value).success,
                errorMessage: 'Enter a channel name from 1 to 64 characters.',
              }),
            );
            configureTile(
              instance,
              chipEditorModel,
              { channel: channelSchema.parse(channel) },
              'Change portal channel',
            );
          } catch {
            /* Cancel retains the channel. */
          }
        },
      },
    ],
    graphics: {
      type,
      draw(g, { meta, connections, context }) {
        defaultTileGraphics(g);
        connectionGraphics(
          g,
          colors.signalColor,
          colors.noSignalColor,
          meta,
          connections,
          ioSides,
          context,
        );
        const high = direction === 'send' ? meta.inputs.left : meta.outputs.right;
        drawExtensionIcon(g, 'portal', high ? 0xc0a5ff : 0x8d76ab, direction);
      },
    },
  };
  return tile;
}
export const portalExtension: DigitilesExtension = {
  id: 'digitiles.portal',
  name: 'Wireless portals',
  version: '1.0.0',
  description:
    'Send signals between distant tiles on the same named channel within one chip. Placed chip instances keep independent channels.',
  capabilities: ['wireless'],
  tiles: [portal('send'), portal('receive')].map((definition) => ({
    definition,
    circuits: [CircuitType.ChipLogic],
  })),
  nodes: [PORTAL_SEND, PORTAL_RECEIVE].map((type) => ({
    type,
    stateSchema: z.null(),
    createDefaultState: () => null,
    process: (input: boolean) => input,
  })),
};
